import { omit, uniqueId } from 'lodash';
import { confirm } from '@dashboard/lib/confirm';
import usePage from '@dashboard/hooks/usePage';
import { feeId, getPageCurrency, transformFeeForApi } from './feeFormValues';
import { useFeeEdit } from './FeeEditContext';
import { useFees } from './FeesContext';

function useFeeHelpers(id) {
  const { page } = usePage();
  const { state: fees, dispatch } = useFees();
  const { saveFees, setIsAddingFee, hasUnsavedChanges, setHasUnsavedChanges } = useFeeEdit();

  const fee = fees.find(item => feeId(item) === id);

  async function confirmDiscard() {
    if (!hasUnsavedChanges) return true;

    return confirm('Discard unsaved changes?', { confirm: 'Discard' });
  }

  async function onCancelAddFee() {
    if (!(await confirmDiscard())) return;

    dispatch({ type: 'delete', fee });
    setHasUnsavedChanges(false);
    setIsAddingFee(false);
  }

  // The API takes the whole list, so the other fees go back exactly as stored.
  async function onSaveFee(values) {
    const savedFees = page.fees || [];
    const body = transformFeeForApi(values, getPageCurrency(page));

    const savedIndex = fee._id ? savedFees.findIndex(item => item._id === fee._id) : -1;
    const position =
      savedIndex >= 0
        ? savedIndex
        : fees.slice(0, fees.indexOf(fee)).filter(item => !item.isNew).length;

    const nextFees =
      savedIndex >= 0
        ? savedFees.map((item, index) => (index === position ? body : item))
        : [...savedFees.slice(0, position), body, ...savedFees.slice(position)];

    const { data, error } = await saveFees(nextFees);

    if (!data || error) {
      return { error: withFeeErrorPaths(error, position) };
    }

    const savedFee =
      (fee._id ? data.fees?.find(item => item._id === fee._id) : data.fees?.[position]) ?? body;

    dispatch({
      type: 'update',
      fee,
      values: { ...savedFee, ...(!savedFee._id && { key: fee.key }), isEditing: true },
    });

    setHasUnsavedChanges(false);
    setIsAddingFee(false);

    return { data, savedFee };
  }

  async function onDeleteFee() {
    if (fee.isNew) {
      await onCancelAddFee();
      return;
    }

    const del = await confirm('Delete fee?', { confirm: 'Delete' });

    if (!del) return;

    const { data, error } = await saveFees((page.fees || []).filter(item => item._id !== fee._id));

    if (!data || error) return;

    dispatch({ type: 'delete', fee });

    if (fee.isEditing) {
      setHasUnsavedChanges(false);
    }
  }

  async function onDuplicateFee() {
    if (!(await confirmDiscard())) return;

    dispatch({
      type: 'add',
      values: [
        {
          ...omit(fee, ['_id', 'stripeProductId']),
          name: `${fee.name} (copy)`,
          key: uniqueId(),
          isEditing: true,
          isNew: true,
        },
      ],
      index: fees.indexOf(fee) + 1,
    });

    setIsAddingFee(true);
    setHasUnsavedChanges(false);
  }

  function onDirty(dirty) {
    if (dirty && fee?.isEditing && !hasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }

    if (!dirty && hasUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  }

  return { onCancelAddFee, onSaveFee, onDeleteFee, onDuplicateFee, onDirty };
}

// The API names a field by its place in the whole list (fees.2.amount); this form holds one fee.
function withFeeErrorPaths(error, position) {
  if (!Array.isArray(error?.errors)) return error;

  const prefix = `fees.${position}.`;

  return {
    ...error,
    errors: error.errors.map(item =>
      item.param?.startsWith(prefix) ? { ...item, param: item.param.slice(prefix.length) } : item,
    ),
  };
}

export default useFeeHelpers;
